import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Target, RotateCw, Trash2, FileDown, CheckSquare, NotebookPen, Send, ArrowRightLeft,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import StimulusCard from "@/components/StimulusCard";
import LearnerConsentPanel from "@/components/LearnerConsentPanel";
import { operantColor, VERBAL_OPERANTS, OPERANT_COLORS } from "@/lib/operants";

export default function LearnerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [learner, setLearner] = useState(null);
  const [assignments, setAssignments] = useState({ targets: [], maintenance: [] });
  const [allCards, setAllCards] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStatus, setPickerStatus] = useState("target");
  const [pickerSelected, setPickerSelected] = useState({});
  const [pickerOperant, setPickerOperant] = useState("all");
  const [saving, setSaving] = useState(false);
  const [newUpdate, setNewUpdate] = useState("");
  const [postingUpdate, setPostingUpdate] = useState(false);

  const load = async () => {
    try {
      const [lRes, aRes, cRes] = await Promise.all([
        http.get(`/learners/${id}`),
        http.get(`/learners/${id}/assignments`),
        http.get("/cards"),
      ]);
      setLearner(lRes.data);
      setAssignments(aRes.data);
      setAllCards(cRes.data);
      // Batch-fetch images for the (unassigned) photo cards shown in the picker.
      const photoIds = (cRes.data || []).filter((c) => (c.card_type || "photo") === "photo").map((c) => c.id);
      if (photoIds.length > 0) {
        try {
          const imgResp = await http.post("/cards/images-by-ids", { ids: photoIds });
          const map = imgResp.data?.images || {};
          setAllCards((prev) => prev.map((c) => (map[c.id] ? { ...c, image_data_url: map[c.id] } : c)));
        } catch (e) { void e; }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load closes over stable state; only id change should refetch.
  useEffect(() => { load(); }, [id]);

  const assignedIds = useMemo(() => {
    const s = new Set();
    assignments.targets.forEach((c) => s.add(c.id));
    assignments.maintenance.forEach((c) => s.add(c.id));
    return s;
  }, [assignments]);

  const setAssignment = async (card_id, status) => {
    try {
      await http.post("/assignments", { card_id, learner_id: id, status });
      toast.success(status === "target" ? "Added to targets" : "Moved to maintenance");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const removeAssignment = async (card_id) => {
    try {
      await http.delete(`/assignments`, { params: { card_id, learner_id: id } });
      load();
    } catch { toast.error("Failed to remove"); }
  };

  const openPicker = (status) => {
    setPickerStatus(status);
    setPickerSelected({});
    setPickerOperant("all");
    setPickerOpen(true);
  };
  const togglePick = (cid) => setPickerSelected((s) => ({ ...s, [cid]: !s[cid] }));
  const selectedIds = Object.keys(pickerSelected).filter((k) => pickerSelected[k]);

  const bulkAssign = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    try {
      const r = await http.post("/assignments/bulk", {
        learner_id: id, card_ids: selectedIds, status: pickerStatus,
      });
      toast.success(`Assigned ${r.data.inserted + r.data.updated} card${r.data.inserted + r.data.updated !== 1 ? "s" : ""}`);
      setPickerOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const filteredPicker = allCards.filter((c) => pickerOperant === "all" || c.verbal_operant === pickerOperant);

  const exportPdf = () => {
    // Cookie is httpOnly; backend also accepts ?auth=. Simpler: use fetch-as-blob so cookies go with it.
    fetch(`${API}/learners/${id}/export.pdf`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error("Export failed");
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${learner.chart_name.replace(/\s+/g, "_")}_program.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  if (!learner) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-6xl">
      <button onClick={() => nav("/learners")}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to learners
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Learner Profile · Chart Name</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            {learner.chart_name}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf}
            className="rounded-full h-11 px-4 sm:px-5" data-testid="export-pdf-button">
            <FileDown className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {learner.notes && (
        <div className="mb-8 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">BCBA reminders</div>
          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{learner.notes}</p>
        </div>
      )}

      <Tabs defaultValue="targets">
        <TabsList className="rounded-full bg-slate-100">
          <TabsTrigger value="targets" className="rounded-full data-[state=active]:bg-white">
            <Target className="w-3.5 h-3.5 mr-1.5" /> Targets ({assignments.targets.length})
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="rounded-full data-[state=active]:bg-white">
            <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Maintenance ({assignments.maintenance.length})
          </TabsTrigger>
          <TabsTrigger value="updates" className="rounded-full data-[state=active]:bg-white">
            <NotebookPen className="w-3.5 h-3.5 mr-1.5" /> Updates ({(learner.target_updates || []).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="updates" className="mt-6">
          <UpdatesTab
            updates={learner.target_updates || []}
            newUpdate={newUpdate}
            setNewUpdate={setNewUpdate}
            postingUpdate={postingUpdate}
            addUpdate={async () => {
              const text = newUpdate.trim();
              if (!text) return;
              setPostingUpdate(true);
              try {
                const r = await http.post(`/learners/${id}/updates`, { text });
                setLearner(r.data);
                setNewUpdate("");
                toast.success("Update saved");
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Failed to save update");
              } finally {
                setPostingUpdate(false);
              }
            }}
            deleteUpdate={async (uid) => {
              try {
                const r = await http.delete(`/learners/${id}/updates/${uid}`);
                setLearner(r.data);
              } catch {
                toast.error("Failed to delete");
              }
            }}
          />
        </TabsContent>

        {["targets", "maintenance"].map((tab) => {
          const status = tab === "targets" ? "target" : "maintenance";
          const list = assignments[tab];
          return (
            <TabsContent key={tab} value={tab} className="mt-6">
              <div className="flex justify-end mb-4">
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => openPicker(status)}
                  data-testid={`assign-${status}`}
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Assign cards
                </Button>
              </div>

              {list.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-2xl p-14 text-center bg-white">
                  <div className="font-display font-bold text-lg">No {tab} yet</div>
                  <p className="text-slate-500 text-sm mt-1">Pick cards from your library to start.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                  {list.map((c) => {
                    const oc = operantColor(c.verbal_operant);
                    return (
                      <div key={c.id} className="relative group">
                        <StimulusCard card={c} size="sm" testId={`assigned-${c.id}`} status={status} />
                        {/* Move / Remove action bar */}
                        <div className="absolute bottom-16 sm:bottom-14 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          {status === "target" ? (
                            <button
                              onClick={() => setAssignment(c.id, "maintenance")}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-900/90 text-white shadow hover:bg-slate-800 backdrop-blur"
                              data-testid={`move-maint-${c.id}`}
                              title="Move this card to Maintenance"
                            >
                              <ArrowRightLeft className="w-3 h-3" /> To Maint.
                            </button>
                          ) : (
                            <button
                              onClick={() => setAssignment(c.id, "target")}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-900/90 text-white shadow hover:bg-slate-800 backdrop-blur"
                              data-testid={`move-target-${c.id}`}
                              title="Move this card back to Targets"
                            >
                              <ArrowRightLeft className="w-3 h-3" /> To Target
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => removeAssignment(c.id)}
                          className="absolute top-2 right-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity bg-white/95 backdrop-blur border border-slate-200 rounded-full p-1.5 text-slate-500 hover:text-rose-600"
                          data-testid={`unassign-${c.id}`}
                          aria-label="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* No-op fragment marker */}
      {/* Multi-select picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">
              Assign cards to {pickerStatus === "target" ? "Targets" : "Maintenance"}
            </DialogTitle>
            <DialogDescription>Multi-select any number of cards from your library.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5 mt-2">
            <button
              onClick={() => setPickerOperant("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
                pickerOperant === "all"
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
              }`}
            >
              All
            </button>
            {VERBAL_OPERANTS.map((op) => {
              const c = OPERANT_COLORS[op];
              const active = pickerOperant === op;
              return (
                <button
                  key={op}
                  onClick={() => setPickerOperant(op)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border-2 transition-colors"
                  style={{
                    background: active ? c.border : c.bg,
                    color: active ? "white" : c.text,
                    borderColor: c.border,
                  }}
                >
                  {op}
                </button>
              );
            })}
          </div>

          {allCards.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No cards yet. Create some in the Card Library first.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 overflow-y-auto p-1">
              {filteredPicker.map((c) => {
                const already = assignedIds.has(c.id);
                const isSel = !!pickerSelected[c.id];
                return (
                  <div key={c.id} className="relative">
                    <StimulusCard
                      card={c}
                      size="sm"
                      selected={isSel}
                      onClick={() => togglePick(c.id)}
                      testId={`picker-card-${c.id}`}
                    />
                    <div className="absolute top-2 right-2 bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1 shadow-sm pointer-events-none">
                      <Checkbox checked={isSel} readOnly />
                    </div>
                    {already && (
                      <div className="absolute bottom-14 right-2 text-[10px] font-bold uppercase bg-slate-900 text-white px-2 py-0.5 rounded-full">
                        Assigned
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter className="mt-4 flex items-center justify-between sm:justify-between">
            <div className="text-sm text-slate-500">
              <CheckSquare className="w-4 h-4 inline mr-1 -mt-0.5" />
              <span className="font-bold text-slate-900">{selectedIds.length}</span> selected
            </div>
            <Button
              onClick={bulkAssign}
              disabled={saving || selectedIds.length === 0}
              className="rounded-full"
              data-testid="bulk-assign-button"
            >
              {saving ? "Assigning…" : `Assign ${selectedIds.length || ""} card${selectedIds.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UpdatesTab({ updates, newUpdate, setNewUpdate, postingUpdate, addUpdate, deleteUpdate }) {
  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") addUpdate();
  };
  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-2">
          New update
        </div>
        <div className="flex gap-2">
          <Input
            value={newUpdate}
            onChange={(e) => setNewUpdate(e.target.value)}
            onKeyDown={onKey}
            placeholder="e.g. Alex mastered 'apple' today — moving to maintenance"
            className="rounded-xl h-11"
            data-testid="update-input"
          />
          <Button
            onClick={addUpdate}
            disabled={postingUpdate || !newUpdate.trim()}
            className="rounded-full h-11 px-5"
            data-testid="add-update-button"
          >
            <Send className="w-4 h-4 mr-2" />
            {postingUpdate ? "Saving…" : "Post"}
          </Button>
        </div>
        <div className="text-xs text-slate-400 mt-2">Cmd/Ctrl + Enter to post</div>
      </div>

      {updates.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center bg-white">
          <div className="font-display font-bold text-lg">No updates yet</div>
          <p className="text-slate-500 text-sm mt-1">
            Log progress notes about current targets — mastery, prompt fading, new attempts.
          </p>
        </div>
      ) : (
        <ol className="mt-6 relative border-l-2 border-slate-200 ml-3">
          {updates.map((u) => (
            <li
              key={u.id}
              className="ml-6 pb-6 relative group"
              data-testid={`update-${u.id}`}
            >
              <span className="absolute -left-[34px] top-1.5 w-3 h-3 rounded-full bg-slate-900 ring-4 ring-white" />
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{u.text}</p>
                  <button
                    onClick={() => deleteUpdate(u.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-rose-600 shrink-0"
                    aria-label="Delete update"
                    data-testid={`delete-update-${u.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-xs text-slate-400 mt-2">
                  {new Date(u.created_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
